import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Dropdown, Label } from 'semantic-ui-react';
import { ThemeContext } from 'styled-components';
import FutureContractsService, { FutureContractType } from '~/api/services/futureContracts';
import i18n from '~/components/i18n';
import Layout from '~/components/Layout';
import TableComponent from '~/components/TableComponent';
import { RouteTypes } from '~/routes/constants';
import { DropdownOptionsType } from '~/types';
import { handleErrors } from '~/utils/helpers';

const getTableColInitialMargin = () => [
  {
    name: i18n.t('Commodity'),
    key: 'symbol',
    type: 'commodity'
  },
  {
    name: i18n.t('Expiration Date'),
    key: 'month',
    type: 'expiration-date',
    format: 'MMM YYYY'
  },
  {
    name: i18n.t('Price Scan Range'),
    key: 'priceScanRange',
    type: 'price-total'
  },
  {
    name: i18n.t('Currency'),
    key: 'clearingCurrency'
  },
  {
    name: i18n.t('Net Change'),
    key: 'netChange',
    type: 'netChange'
  },
  {
    name: i18n.t('Actions')
  }
];

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Commodity'),
    key: 'symbol',
    type: 'commodity'
  },
  {
    name: i18n.t('Expiration Date'),
    key: 'timestamp',
    type: 'expiration-date',
    format: 'MMM YYYY'
  },
  {
    name: i18n.t('Settlement'),
    key: 'settlement',
    type: 'price-total'
  },
  {
    name: i18n.t('Currency'),
    key: 'currency'
  },
  {
    name: i18n.t('Net Change'),
    key: 'netChange',
    type: 'netChange'
  },
  {
    name: i18n.t('Actions')
  }
];

const initialMarginsSymbol = {
  abbreviation: 'KFMV',
  label: 'Ife KFMV',
  slug: ''
}

const FutureContracts: React.FC = () => {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const { contractsType } = useParams();
  const navigate = useNavigate();
  const { firstColor } = useContext(ThemeContext)
  const actions = [
    {
      popupMessage: t('Chart'),
      type: 'icon',
      name: 'chart line',
      size: 'large',
      link: RouteTypes.FUTURE_CONTRACTS_TYPES_CHART,
      actionType: 'chart'
    }
  ];

  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    symbol: '',
    date: ''
  });
  const [symbolOptions, setSymbolOptions] = useState<DropdownOptionsType[]>([]);
  const [dateOptions, setDateOptions] = useState<DropdownOptionsType[]>([]);
  const [rows, setRows] = useState<FutureContractType[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    fetchFutureContractData();
  }, [contractsType]);

  async function fetchFutureContractData() {
    setIsBusy(true);
    try {
      const responseOptions = await FutureContractsService.getIndexData(contractsType);
      const symbols = responseOptions.data.symbols ? responseOptions.data.symbols : [initialMarginsSymbol]
      setDateOptions(
        responseOptions.data.dates.map((date) => ({
          text: dayjs(date).format('DD.MM.YYYY'),
          value: date
        }))
      );
      setSymbolOptions(symbols?.map((symbol) => ({
        text: symbol.label,
        value: symbol.abbreviation
      })));
      setDataFiltered({
        page: 0,
        rows: 30,
        symbol: symbols[0].abbreviation,
        date: responseOptions.data.dates[0]
      });
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  async function fetchFilteredData() {
    setIsFetching(true);
    try {
      const responseList = await FutureContractsService.getMarginList(contractsType, {
        ...dataFiltered,
        page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
      });
      setRows(responseList.data);
    } catch (error) {
      handleErrors(error)
    }
    setIsFetching(false);
  }

  useEffect(() => {
    if (dataFiltered.symbol !== '') {
      fetchFilteredData();
    }
  }, [dataFiltered]);

  function renderSymbolFilter(): JSX.Element {
    return (
      <Dropdown
        placeholder={t('Select symbol')}
        selection
        value={dataFiltered.symbol}
        options={symbolOptions}
        style={{ marginRight: 20 }}
        selectOnBlur={false}
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, symbol: data.value as string })}
      />
    );
  }

  function renderDatesFilters(): JSX.Element {
    return (
      <Dropdown
        placeholder={t('Select date')}
        options={dateOptions}
        value={dataFiltered.date}
        selection
        selectOnBlur={false}
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, date: data.value as string })}
      />
    );
  }

  function renderFilters() {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {renderSymbolFilter()}
        {renderDatesFilters()}
      </div>
    );
  }

  function handleActionClick(row: FutureContractType, type: string) {
    const { symbol, year, month, expiryMonth, expiryYear } = row;
    switch (type) {
      case 'chart':
        return navigate(
          RouteTypes.FUTURE_CONTRACTS_TYPES_CHART
            .replace(':contractsType', contractsType)
            .replace(':symbol', symbol)
            .replace(':month', (month || expiryMonth)?.toString())
            .replace(':year', (year || expiryYear).toString())
        );
      default:
        return;
    }
  }

  function renderTable(): JSX.Element {
    return (
      <div style={{ overflow: 'auto' }}>
        <TableComponent
          tableColumnHeaders={
            contractsType === 'initial-margins'
              ? getTableColInitialMargin()
              : getTableColumnHeaders()
          }
          idKey={'createdAt'}
          animated
          rows={rows}
          isFetchingData={isFetching}
          removePagination
          dataFiltered={dataFiltered}
          handleChangeDataFiltered={(data) => setDataFiltered(data)}
          actions={actions}
          handleClickAction={(row, type) => handleActionClick(row, type)}
          options={symbolOptions}
          total={rows.length || 0}
        />
      </div>
    );
  }

  function getTitle() {
    switch (contractsType) {
      case 'ife-quotes':
        return t('Ife stock milk value')
      case 'initial-margins':
        return t('Initial margin')
      case 'eod-quotes':
        return t('EEX Setlements')
    }
  }

  const updatedDate = rows[0]?.createdAt ? dayjs(rows[0]?.createdAt).format('DD.MM.YYYY HH:mm') : ''

  return (
    <Layout
      title={getTitle()}
      isBusy={isBusy}
      segmented
      filters={(symbolOptions.length > 0 || dateOptions.length > 0) && renderFilters()}
    >
      <Label basic style={{ margin: '1em 0em', background: firstColor, color: 'white' }}>
        {t('Updated: {{updatedDate}}', { updatedDate })}
      </Label>
      {!isBusy && renderTable()}
    </Layout>
  );
};

export default FutureContracts;
